import { getSessionHeaders } from '../serverSession';

// API helper for competition operations - NO getServiceRoleClient() in browser
async function callCompetitionAPI(operation: string, data: Record<string, any> = {}): Promise<any> {
 const response = await fetch('/api/user/competitions', {
 method: 'POST',
 headers: { 'Content-Type': 'application/json', ...getSessionHeaders() },
 body: JSON.stringify({ operation, data })
 });
 const result = await response.json();
 if (!response.ok) {
 throw new Error(result.error || 'API call failed');
 }
 return result;
}

export interface Competition {
 id: string;
 name: string;
 description: string;
 phase_id: number;
 start_date: string;
 end_date?: string;
 is_active: boolean;
 minimum_qualification_amount: number;
 total_prize_pool: number;
 max_participants?: number;
 status: 'upcoming' | 'active' | 'ended' | 'cancelled';
 total_participants: number;
 qualified_participants: number;
 created_at: string;
 updated_at: string;
}

export interface PrizeTier {
 id: string;
 competition_id: string;
 tier_name: string;
 tier_rank_start: number;
 tier_rank_end: number;
 prize_amount: number;
 prize_type: string;
 display_order: number;
 emoji: string;
}

export interface CompetitionParticipant {
 id: string;
 competition_id: string;
 user_id: number;
 username?: string;
 full_name?: string;
 total_referral_volume: number;
 direct_referrals_count: number;
 qualified_referrals_count: number;
 is_qualified: boolean;
 current_rank?: number;
 final_rank?: number;
 joined_at: string;
 last_updated: string;
}

export interface LeaderboardEntry {
 competition_id: string;
 user_id: number;
 username: string;
 full_name: string;
 total_referral_volume: number;
 direct_referrals_count: number;
 qualified_referrals_count: number;
 is_qualified: boolean;
 current_rank?: number;
 calculated_rank: number;
 joined_at: string;
}

export interface CompetitionStats {
 totalParticipants: number;
 qualifiedParticipants: number;
 leadingVolume: number;
 totalPrizePool: number;
 minimumQualification: number;
}

// ===== Multi-Tier Competition Interfaces (Phase 5+) =====

export interface TierConfig {
 tier_level: number;
 tier_name: string;
 tier_emoji: string; // Note: the get_tiered_leaderboard RPC aliases the DB column `emoji` as `tier_emoji`
 volume_min: number;
 volume_max: number | null; // null = uncapped (e.g. Master Diggers $60,000+)
 pool_allocation_percent: number;
 min_direct_buyers: number;
 tier_pool: number; // Calculated: totalRevenue * 0.15 * (pool_allocation_percent / 100)
}

export interface TieredLeaderboardEntry {
 user_id: number;
 username: string;
 total_volume: number;
 total_referrals: number;
 rank: number;
 prize_tier: string;
}

export interface TieredLeaderboardTier extends TierConfig {
 leaderboard: TieredLeaderboardEntry[];
}

export interface TieredLeaderboardResponse {
 success: boolean;
 phase: any;
 tiered: true;
 tiers: TieredLeaderboardTier[];
 metrics: {
 commissions_paid: number;
 active_competitors: number;
 calculated_shares_sold: number;
 total_revenue: number;
 };
}

export interface FlatLeaderboardResponse {
 success: boolean;
 phase: any;
 tiered?: false;
 leaderboard: Array<{
 user_id: number;
 username: string;
 total_volume: number;
 total_referrals: number;
 rank: number;
 prize_tier: string;
 isPlaceholder?: boolean;
 }>;
 metrics: {
 commissions_paid: number;
 active_competitors: number;
 calculated_shares_sold?: number;
 total_revenue?: number;
 };
}

// ===== Single Pool Top 40 Competition Interface (Phase 5+ v4.1.0) =====

// ===== Single Pool Top 50 Interfaces (Phase 5+ v4.2.0) =====

export interface SinglePoolLeaderboardEntry {
 user_id: number;
 username: string;
 total_volume: number;
 total_referrals: number;
 rank: number;
 prize_tier: string;
 prize_percent: number;
 isPlaceholder?: boolean;
}

export interface SinglePoolLeaderboardResponse {
 success: boolean;
 phase: any;
 singlePool: true;
 leaderboard: SinglePoolLeaderboardEntry[];
 prizePool: number;
 config: SinglePoolConfig;
 metrics: {
  commissions_paid: number;
  active_competitors: number;
  calculated_shares_sold: number;
  total_revenue: number;
 };
}

export type LeaderboardResponse = SinglePoolLeaderboardResponse | TieredLeaderboardResponse | FlatLeaderboardResponse;

class CompetitionService {
 /**
 * Get the current active competition
 */
 async getCurrentCompetition(): Promise<Competition | null> {
 try {
 const result = await callCompetitionAPI('getCurrentCompetition');
 return result.competition;
 } catch (error) {
 console.error('Error in getCurrentCompetition:', error);
 return null;
 }
 }

 /**
 * Get prize tiers for a competition
 */
 async getPrizeTiers(competitionId: string): Promise<PrizeTier[]> {
 try {
 const result = await callCompetitionAPI('getPrizeTiers', { competitionId });
 return result.prizeTiers || [];
 } catch (error) {
 console.error('Error in getPrizeTiers:', error);
 return [];
 }
 }

 /**
 * Get leaderboard for a competition with real commission data
 */
 async getLeaderboard(competitionId: string, limit: number = 10): Promise<LeaderboardEntry[]> {
 try {
 // Try to get competition leaderboard first
 const result = await callCompetitionAPI('getLeaderboard', { competitionId, limit });

 if (result.leaderboard && result.leaderboard.length > 0) {
 return result.leaderboard;
 }

 // Fall back to real commission data (cumulative)
 return await this.getRealCommissionLeaderboard(limit);
 } catch (error) {
 console.error('Error in getLeaderboard:', error);
 return await this.getRealCommissionLeaderboard(limit);
 }
 }

 /**
 * Get phase-specific competition stats using existing data structure
 */
 private async getPhaseSpecificStats(
 _phaseId: number,
 totalPrizePool: number,
 minimumQualification: number
 ): Promise<CompetitionStats> {
 try {
 // Get total users count via API
 const result = await callCompetitionAPI('getUserCount');
 const totalUsers = result.count || 0;

 return {
 totalParticipants: totalUsers,
 qualifiedParticipants: 0,
 leadingVolume: 0,
 totalPrizePool,
 minimumQualification
 };
 } catch (error) {
 console.error('Error in getPhaseSpecificStats:', error);
 return {
 totalParticipants: 0,
 qualifiedParticipants: 0,
 leadingVolume: 0,
 totalPrizePool,
 minimumQualification
 };
 }
 }

 /**
 * Get real commission leaderboard from database
 */
 private async getRealCommissionLeaderboard(limit: number = 10): Promise<LeaderboardEntry[]> {
 try {
 const result = await callCompetitionAPI('getRealCommissionLeaderboard', { limit });
 return result.leaderboard || [];
 } catch (error) {
 console.error('Error getting real commission leaderboard:', error);
 return [];
 }
 }

 /**
 * Get competition statistics with real user data
 */
 async getCompetitionStats(competitionId: string): Promise<CompetitionStats> {
 try {
 const result = await callCompetitionAPI('getCompetitionStats', { competitionId });

 if (result.stats) {
 // If we got stats from competition tables, check if there's data
 if (result.stats.qualifiedParticipants > 0 || result.stats.leadingVolume > 0) {
 return {
 totalParticipants: result.stats.totalParticipants,
 qualifiedParticipants: result.stats.qualifiedParticipants,
 leadingVolume: result.stats.leadingVolume,
 totalPrizePool: result.stats.totalPrizePool,
 minimumQualification: result.stats.minimumQualification
 };
 }

 // If we have a phase_id, try phase-specific stats
 if (result.stats.phaseId) {
 const phaseStats = await this.getPhaseSpecificStats(
 result.stats.phaseId,
 result.stats.totalPrizePool || 150000,
 result.stats.minimumQualification || 2500
 );

 if (phaseStats.qualifiedParticipants > 0 || phaseStats.leadingVolume > 0) {
 return phaseStats;
 }
 }

 // Fall back to real user data (cumulative)
 return await this.getRealUserStats(
 result.stats.totalPrizePool || 150000,
 result.stats.minimumQualification || 2500
 );
 }

 // No stats returned, fall back
 return await this.getRealUserStats(150000, 2500);
 } catch (error) {
 console.error('Error in getCompetitionStats:', error);
 return await this.getRealUserStats(150000, 2500);
 }
 }

 /**
 * Get real user statistics from the database
 */
 private async getRealUserStats(totalPrizePool: number, minimumQualification: number): Promise<CompetitionStats> {
 try {
 const result = await callCompetitionAPI('getRealUserStats', { totalPrizePool, minimumQualification });

 return {
 totalParticipants: result.stats?.totalParticipants || 0,
 qualifiedParticipants: result.stats?.qualifiedParticipants || 0,
 leadingVolume: result.stats?.leadingVolume || 0,
 totalPrizePool,
 minimumQualification
 };
 } catch (error) {
 console.error('Error getting real user stats:', error);
 return {
 totalParticipants: 0,
 qualifiedParticipants: 0,
 leadingVolume: 0,
 totalPrizePool,
 minimumQualification
 };
 }
 }

 /**
 * Add or update a participant in the competition
 */
 async updateParticipant(
 competitionId: string,
 userId: number,
 referralVolume: number,
 directReferrals: number = 0,
 qualifiedReferrals: number = 0
 ): Promise<boolean> {
 try {
 const result = await callCompetitionAPI('updateParticipant', {
 competitionId,
 userId,
 referralVolume,
 directReferrals,
 qualifiedReferrals
 });
 return result.success;
 } catch (error) {
 console.error('Error in updateParticipant:', error);
 return false;
 }
 }

 /**
 * Create a new competition for a phase
 * This creates a competition that rewards network leaders based on referral sales volume
 */
 async createCompetition(
 name: string,
 description: string,
 phaseId: number,
 totalPrizePool: number,
 minimumQualification: number = 2500,
 endDate?: string
 ): Promise<string | null> {
 try {
 const result = await callCompetitionAPI('createCompetition', {
 name,
 description,
 phaseId,
 totalPrizePool,
 minimumQualification,
 endDate
 });

 if (result.success && result.competitionId) {
 return result.competitionId;
 }
 return null;
 } catch (error) {
 console.error('Error in createCompetition:', error);
 return null;
 }
 }

 /**
 * Get all competitions (for admin interface)
 */
 async getAllCompetitions(): Promise<Competition[]> {
 try {
 const result = await callCompetitionAPI('getAllCompetitions');
 return result.competitions || [];
 } catch (error) {
 console.error('Error in getAllCompetitions:', error);
 return [];
 }
 }

 /**
 * Get competition by ID
 */
 async getCompetitionById(competitionId: string): Promise<Competition | null> {
 try {
 const result = await callCompetitionAPI('getCompetitionById', { competitionId });
 return result.competition || null;
 } catch (error) {
 console.error('Error in getCompetitionById:', error);
 return null;
 }
 }

 /**
 * Update competition status
 */
 async updateCompetitionStatus(
 competitionId: string,
 status: 'upcoming' | 'active' | 'ended' | 'cancelled',
 endDate?: string
 ): Promise<boolean> {
 try {
 const result = await callCompetitionAPI('updateCompetitionStatus', {
 competitionId,
 status,
 endDate
 });
 return result.success;
 } catch (error) {
 console.error('Error in updateCompetitionStatus:', error);
 return false;
 }
 }

 /**
 * Setup default prize tiers for a competition
 */
 async setupDefaultPrizeTiers(competitionId: string): Promise<boolean> {
 try {
 const result = await callCompetitionAPI('setupDefaultPrizeTiers', { competitionId });
 return result.success;
 } catch (error) {
 console.error('Error in setupDefaultPrizeTiers:', error);
 return false;
 }
 }

 // ===== Multi-Tier Competition Methods (Phase 5+) =====

 /**
 * Fetch public leaderboard data (handles both flat and tiered responses)
 */
 async fetchPublicLeaderboard(phaseId: number): Promise<LeaderboardResponse> {
 try {
 const response = await fetch(`/api/public/leaderboard?phaseId=${phaseId}&type=leaderboard`);
 const result = await response.json();
 if (!response.ok) {
 throw new Error(result.error || 'Failed to fetch leaderboard');
 }
 return result as LeaderboardResponse;
 } catch (error) {
 console.error('Error fetching public leaderboard:', error);
 throw error;
 }
 }

 /**
 * Check if a leaderboard response is tiered (Phase 5+)
 */
 isTieredResponse(response: LeaderboardResponse): response is TieredLeaderboardResponse {
 return 'tiered' in response && response.tiered === true;
 }

 /**
 * Get tier config for a competition via admin API
 */
 async getTierConfig(competitionId: string): Promise<TierConfig[]> {
 try {
 const result = await callCompetitionAPI('getTierConfig', { competitionId });
 return result.tierConfig || [];
 } catch (error) {
 console.error('Error in getTierConfig:', error);
 return [];
 }
 }

 /**
 * Save tier config for a competition via admin API
 */
 async saveTierConfig(competitionId: string, tiers: Omit<TierConfig, 'tier_pool'>[]): Promise<boolean> {
 try {
 const result = await callCompetitionAPI('saveTierConfig', { competitionId, tiers });
 return result.success;
 } catch (error) {
 console.error('Error in saveTierConfig:', error);
 return false;
 }
 }
 /**
 * Load investment phases via API
 */
 async loadPhases(): Promise<any[]> {
 try {
 const result = await callCompetitionAPI('loadPhases');
 return result.phases || [];
 } catch (error) {
 console.error('Error in loadPhases:', error);
 return [];
 }
 }

 /**
 * Delete a competition and all related records (prize tiers, tier configs)
 */
 async deleteCompetition(competitionId: string): Promise<boolean> {
 try {
 const result = await callCompetitionAPI('deleteCompetition', { competitionId });
 return result.success;
 } catch (error) {
 console.error('Error in deleteCompetition:', error);
 return false;
 }
 }

 /**
 * Create a competition with prize tiers and optional tier configs in one API call
 */
 async createCompetitionFull(params: {
 name: string;
 description: string;
 phaseId: number;
 totalPrizePool: number;
 minimumQualification?: number;
 prizeTiers?: any[];
 tierConfigs?: any[];
 }): Promise<any> {
 try {
 const result = await callCompetitionAPI('createCompetitionFull', params);
 return result;
 } catch (error) {
 console.error('Error in createCompetitionFull:', error);
 return { success: false };
 }
 }

 /**
 * Get single pool config for a Phase 5+ competition
 */
 async getSinglePoolConfig(competitionId: string): Promise<SinglePoolConfig | null> {
 try {
 const result = await callCompetitionAPI('getSinglePoolConfig', { competitionId });
 return result.config || null;
 } catch (error) {
 console.error('Error in getSinglePoolConfig:', error);
 return null;
 }
 }

 /**
 * Save single pool config for a Phase 5+ competition
 */
 async saveSinglePoolConfig(competitionId: string, config: SinglePoolConfig): Promise<boolean> {
 try {
 const result = await callCompetitionAPI('saveSinglePoolConfig', { competitionId, config });
 return result.success;
 } catch (error) {
 console.error('Error in saveSinglePoolConfig:', error);
 return false;
 }
 }
}

// ===== Single Pool Config Type (Phase 5+ v4.2.0) =====
export interface SinglePoolConfig {
 prize_pool_percent: number;
 payout_percentages: Record<string | number, number>;
}

export const DEFAULT_SINGLE_POOL_CONFIG: SinglePoolConfig = {
 prize_pool_percent: 15,
 payout_percentages: {
  1: 15, 2: 12, 3: 10,
  '4-10': 3, '11-20': 2, '21-30': 1, '31-50': 0.6
 }
};

export const competitionService = new CompetitionService();
